import React, { useRef, useState } from "react";
import { FormInstance } from "antd";
import { FormField, FormRow } from "components/common/forms";
import useMount from "hooks/useMount";
import { countryOptions, getPostalCodeRegex, stateOptions } from "services/GeoData";
import { Address, addressFields, BillingAddress } from "models/Address";
import { FormChange } from "components/common/forms/FormChange";
import { FormFieldsOf } from "helpers/ModelBuilder";
import { pick } from "lodash";

type Props = {
  initialValues: Address | BillingAddress
  parentForm: FormInstance;
  fieldSet?: FormFieldsOf<Address | BillingAddress>;
}
const AddressSection: React.FC<Props> = ({ initialValues, parentForm, fieldSet }) => {
  const fields = useFields(addressFields, fieldSet);
  const countryOps = countryOptions();
  const [stateOps, setStateOps] = useState([]);
  const [postalRegex, setPostalRegex] = useState<RegExp>(/./);
  const fieldKeys = Object.keys(fieldSet || addressFields);

  useMount(() => {
    if (initialValues) {
      const values = pick(initialValues, fieldKeys);
      parentForm.setFieldsValue(values);
      const country = values[fields.country.name];
      if (country) {
        setStateOps(stateOptions(country));
        setPostalRegex(getPostalCodeRegex(country));
      }
    }
  });

  const onValuesChange = async (changedValues: Partial<Address>, values: Address) => {
    if (fields.country.name in changedValues) {
      const country = changedValues[fields.country.name];
      parentForm.setFieldsValue({
        [fields.state.name]: null,
        [fields.city.name]: '',
        [fields.postalCode.name]: '',
      });
      const ops = stateOptions(country);
      const regexs = getPostalCodeRegex(country);
      setStateOps(ops);
      setPostalRegex(regexs);
    }
    if (fields.postalCode.name in changedValues) {
      const postalCode = changedValues[fields.postalCode.name];
      if (postalCode) {
        const postal = postalCode.toUpperCase();
        const value = postalRegex.test(postal) ? postal : postal.slice(0, -1);
        parentForm.setFieldsValue({ postalCode: value });
      }
    }
  }

  return (
    <>
      <FormChange onValuesChange={onValuesChange} />

      <FormField field={fields.address} />
      <FormField field={fields.address2} />
      <FormRow colSpans={[12, 12]} gap={16}>
        <FormField field={fields.country} options={countryOps} />
        <FormField field={fields.state} options={stateOps} />
      </FormRow>
      <FormRow colSpans={[12, 12]} gap={16}>
        <FormField field={fields.city} />
        <FormField field={fields.postalCode} />
      </FormRow>
    </>
  );
};

export default AddressSection;

function useFields<T, U>(baseFields: FormFieldsOf<T>, fieldSet: FormFieldsOf<U>) {
  const fieldsRef = useRef<FormFieldsOf<T>>();

  if (!fieldsRef.current) {
    const keys = Object.keys(baseFields);
    const values = Object.values(fieldSet || baseFields);
    const entries = keys.map((key, idx) => [key, values[idx]]);
    fieldsRef.current = Object.fromEntries(entries);
  }
  return fieldsRef.current;
}
